import { CalendarDays, CheckCircle2, ExternalLink, Lock, Plus, Presentation, Settings2, X } from 'lucide-react';
import * as React from 'react';
import {
  devConnectState,
  formatPlainDate,
  formatSessionDate,
  isFriday,
  nextFriday,
  today,
  type PlainDate,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { CalendarArt } from '@/components/illustrations';
import { PlainDatePicker, TimeSelect } from '@/components/plain-date-picker';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { DEV_CONNECT_STATE_LABELS, PILL_CLASS, devConnectStateVariant } from '@/lib/badges';
import {
  useBookSlot,
  useCreateSession,
  useDevConnectSessions,
  useMarkSessionHeld,
  useReleaseSlot,
  useShowcaseLog,
  useUpdateSession,
  type DevConnectBooking,
  type DevConnectSession,
  type ShowcaseEntry,
} from '@/lib/dev-connect-api';
import { useCurrentUser } from '@/lib/session';

const err = (e: unknown) => (e instanceof Error ? e.message : undefined);

/** "9:00am" from a 24-hour HH:MM. */
function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const hour = (h ?? 0) % 12 === 0 ? 12 : (h ?? 0) % 12;
  return `${hour}:${String(m ?? 0).padStart(2, '0')}${(h ?? 0) < 12 ? 'am' : 'pm'}`;
}

/** "Thu 9:00am" - the deadline, said the way somebody reads a sign-up sheet. */
function formatCloses(closesAt: string): string {
  const d = new Date(closesAt);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/**
 * Why the sheet will not take this person's booking, in their own terms.
 *
 * Every one of these is a sentence rather than a state name, because the whole
 * point of showing a disabled slot instead of a clickable one is that the person
 * learns why without having to click it and read an error.
 */
function blockedMessage(session: DevConnectSession): string | null {
  switch (session.bookBlockedReason) {
    case 'CLOSED':
      return `Booking closed ${formatCloses(session.closesAt)}.`;
    case 'FULL':
      return 'Every slot is taken.';
    case 'ALREADY_BOOKED':
      return 'You already have a slot at this session.';
    case 'DEPARTMENT':
      return session.allowedDepartments.length
        ? `Slots this week are for ${session.allowedDepartments.map((d) => d.name).join(', ')}.`
        : 'This session is not open for booking.';
    case 'HELD':
      return 'This session has already happened.';
    default:
      return null;
  }
}

/**
 * Clearing somebody else's slot.
 *
 * The reason is required by the service, not optional garnish: this takes a slot
 * off a person who may have spent the week preparing, and the same bar applies as
 * one_on_one.not_held. Months later an honest "we cut to three slots for the
 * customer demo" and a grudge have to read differently in the audit log.
 *
 * Releasing your OWN slot asks for nothing - you cannot take something away from
 * yourself - which is why this dialog only ever appears for somebody else's.
 */
function ClearSlotDialog({
  booking,
  heldOn,
  open,
  onOpenChange,
}: {
  booking: DevConnectBooking | null;
  heldOn: PlainDate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [reason, setReason] = React.useState('');
  const release = useReleaseSlot();

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking) return;
    release
      .mutateAsync({ id: booking.id, reason: reason.trim() })
      .then(() => {
        toast.success(`${booking.presenter.name}'s slot cleared`);
        onOpenChange(false);
      })
      .catch((e) => toast.error('Could not clear', { description: err(e) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Clear {booking?.presenter.name}&rsquo;s slot</DialogTitle>
            <DialogDescription>
              {formatSessionDate(heldOn)} &middot; &ldquo;{booking?.title}&rdquo;. They will be
              told, and the reason goes on the record with your name.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            <Label htmlFor="dc-clear-reason">Why are you clearing it?</Label>
            <Textarea
              id="dc-clear-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Cut to three slots to make room for the customer demo."
              rows={3}
              maxLength={1000}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Clear this booking"}
              type="submit"
              variant="destructive"
              disabled={reason.trim().length < 3 || release.isPending}
            >
              {release.isPending ? 'Clearing…' : 'Clear the slot'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Session settings. SUPER_ADMIN only, and the whole reason the start time is not a
 * constant: a week that moves to the afternoon moves its booking deadline with it.
 */
/**
 * Open a session. The only way one comes into existence.
 *
 * Nothing generates sessions any more: they run roughly fortnightly and which
 * Friday is a human decision, so somebody has to say. The API route has always
 * existed but had no UI, because while the worker opened every Friday an admin
 * only ever needed to EDIT one.
 *
 * Departments are deliberately not on this form. Omitting them tells the API to
 * use the configured default, which is the answer in almost every case; the few
 * sessions that are for one team are narrowed afterwards, and a picker here would
 * put an empty-means-nobody trap on the happy path.
 */
function NewSessionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Defaults to the coming Friday at 9am - the usual answer, so opening the next
  // session is normally a two-click job.
  const [heldOn, setHeldOn] = React.useState(() => nextFriday(today()));
  const [startsAtLocal, setStartsAtLocal] = React.useState('09:00');
  const [capacity, setCapacity] = React.useState('4');
  const [theme, setTheme] = React.useState('');
  const create = useCreateSession();

  React.useEffect(() => {
    if (open) {
      setHeldOn(nextFriday(today()));
      setStartsAtLocal('09:00');
      setCapacity('4');
      setTheme('');
    }
  }, [open]);

  const notAFriday = heldOn !== '' && !isFriday(heldOn);
  const inThePast = heldOn !== '' && heldOn < today();
  const nextCapacity = Number(capacity);
  const capacityValid = Number.isInteger(nextCapacity) && nextCapacity >= 1 && nextCapacity <= 20;

  // Booking shuts 24h before the start, so a session opened for tomorrow morning is
  // already closed to everyone but an admin. Worth saying before they save it.
  const closesAt =
    heldOn !== '' ? new Date(new Date(`${heldOn}T${startsAtLocal}:00+05:30`).getTime() - 24 * 3600_000) : null;
  const closesInThePast = closesAt !== null && closesAt.getTime() < Date.now();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create
      .mutateAsync({
        heldOn,
        startsAtLocal,
        capacity: nextCapacity,
        theme: theme.trim() || null,
      })
      .then((session) => {
        toast.success(`${formatSessionDate(session.heldOn)} is open`);
        onOpenChange(false);
      })
      .catch((e) => toast.error('Could not open the session', { description: err(e) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Open a session</DialogTitle>
            <DialogDescription>
              Puts a Friday on the sheet so people can claim a slot. Booking closes 24 hours
              before it starts.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dc-new-date">Date</Label>
              <PlainDatePicker id="dc-new-date" value={heldOn} onChange={setHeldOn} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-new-start">Start time</Label>
              <TimeSelect id="dc-new-start" value={startsAtLocal} onChange={setStartsAtLocal} />
              <p className="text-xs text-muted-foreground">
                Asia/Kolkata.
                {closesAt && !closesInThePast
                  ? ` Booking closes ${formatCloses(closesAt.toISOString())}.`
                  : ''}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-new-capacity">Slots</Label>
              <Input
                id="dc-new-capacity"
                type="number"
                min={1}
                max={20}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Three or four demos fit in an hour.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-new-theme">Theme (optional)</Label>
              <Input
                id="dc-new-theme"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="Anything you shipped in Q2"
                maxLength={200}
              />
            </div>

            {inThePast && (
              <Alert variant="destructive">
                <AlertDescription>
                  That date has already passed. Nobody will be able to book it.
                </AlertDescription>
              </Alert>
            )}
            {notAFriday && !inThePast && (
              <Alert>
                <AlertDescription>
                  That is not a Friday. Fine for a one-off — Dev Connect is usually a Friday,
                  so it is worth a second look.
                </AlertDescription>
              </Alert>
            )}
            {closesInThePast && !inThePast && (
              <Alert>
                <AlertDescription>
                  That is less than 24 hours away, so booking is already closed to everyone
                  but you.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Open the session so people can book it"} type="submit" disabled={create.isPending || heldOn === '' || !capacityValid}>
              {create.isPending ? 'Opening…' : 'Open it'}
            </Button>
            {(heldOn === '' || !capacityValid) && <span className="self-center text-xs text-muted-foreground">{heldOn === '' ? 'Choose the date first' : 'Capacity must be a whole number from 1 to 20'}</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({
  session,
  open,
  onOpenChange,
}: {
  session: DevConnectSession;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [heldOn, setHeldOn] = React.useState(session.heldOn);
  const [startsAtLocal, setStartsAtLocal] = React.useState(session.startsAtLocal);
  const [capacity, setCapacity] = React.useState(String(session.capacity));
  const [theme, setTheme] = React.useState(session.theme ?? '');
  const update = useUpdateSession();

  React.useEffect(() => {
    if (open) {
      setHeldOn(session.heldOn);
      setStartsAtLocal(session.startsAtLocal);
      setCapacity(String(session.capacity));
      setTheme(session.theme ?? '');
    }
  }, [open, session.heldOn, session.startsAtLocal, session.capacity, session.theme]);

  const moving = heldOn !== session.heldOn;
  // Dev Connect is a Friday ritual. Nothing enforces it - a holiday week or an
  // all-hands genuinely lands elsewhere, and refusing that just means the session
  // happens off the record - but it is worth saying out loud, since it is usually
  // a slip.
  const notAFriday = heldOn !== '' && !isFriday(heldOn);
  const movingInThePast = moving && heldOn < today();

  const taken = session.bookings.length;
  const nextCapacity = Number(capacity);
  const shrinkingBelowBookings =
    Number.isFinite(nextCapacity) &&
    session.bookings.some((b) => b.slotIndex >= nextCapacity);

  // Moving the start earlier can put the 24h deadline in the past, which closes
  // booking for everyone the moment it saves. Said out loud rather than discovered.
  const movedDeadline =
    startsAtLocal !== session.startsAtLocal || moving
      ? new Date(new Date(`${heldOn}T${startsAtLocal}:00+05:30`).getTime() - 24 * 3600_000)
      : null;
  const closesInThePast = movedDeadline !== null && movedDeadline.getTime() < Date.now();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!session.id) return;
    update
      .mutateAsync({
        id: session.id,
        heldOn,
        startsAtLocal,
        capacity: nextCapacity,
        theme: theme.trim() || null,
      })
      .then(() => {
        toast.success('Session updated');
        onOpenChange(false);
      })
      .catch((e) => toast.error('Could not update', { description: err(e) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{formatSessionDate(session.heldOn)}</DialogTitle>
            <DialogDescription>
              Booking closes 24 hours before the session starts.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dc-date">Date</Label>
              <PlainDatePicker id="dc-date" value={heldOn} onChange={setHeldOn} />
              {moving && session.bookings.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {session.bookings.length}{' '}
                  {session.bookings.length === 1 ? 'booking moves' : 'bookings move'} with it, and{' '}
                  {session.bookings.length === 1 ? 'that presenter is' : 'those presenters are'}{' '}
                  told.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-start">Start time</Label>
              <TimeSelect id="dc-start" value={startsAtLocal} onChange={setStartsAtLocal} />
              <p className="text-xs text-muted-foreground">
                Asia/Kolkata. Booking will close{' '}
                {formatCloses(
                  (movedDeadline ?? new Date(session.closesAt)).toISOString(),
                )}
                .
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-capacity">Slots</Label>
              <Input
                id="dc-capacity"
                type="number"
                min={1}
                max={20}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
              {taken > 0 && (
                <p className="text-xs text-muted-foreground">
                  {taken} {taken === 1 ? 'slot is' : 'slots are'} taken.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dc-theme">Theme (optional)</Label>
              <Input
                id="dc-theme"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="Anything you shipped in Q2"
                maxLength={200}
              />
            </div>

            {shrinkingBelowBookings && (
              <Alert variant="destructive">
                <AlertDescription>
                  Someone is booked into a slot above {nextCapacity}. Release it first —
                  shrinking the session will be refused.
                </AlertDescription>
              </Alert>
            )}
            {notAFriday && (
              <Alert>
                <AlertDescription>
                  That is not a Friday. Fine for a one-off — Dev Connect is usually a
                  Friday, so it is worth a second look.
                </AlertDescription>
              </Alert>
            )}
            {movingInThePast && (
              <Alert variant="destructive">
                <AlertDescription>
                  That date has already passed. Nobody will be able to book it.
                </AlertDescription>
              </Alert>
            )}
            {closesInThePast && !shrinkingBelowBookings && !movingInThePast && (
              <Alert>
                <AlertDescription>
                  That start time puts the deadline in the past, so booking closes
                  immediately for everyone but you.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={update.isPending || shrinkingBelowBookings || heldOn === ''}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            {(shrinkingBelowBookings || heldOn === '') && <span className="self-center text-xs text-muted-foreground">{heldOn === '' ? 'Choose the date first' : 'Capacity cannot go below the people already booked'}</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Recording what actually happened is what puts a Friday into the showcase log. */
function MarkHeldDialog({
  session,
  open,
  onOpenChange,
}: {
  session: DevConnectSession;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [notes, setNotes] = React.useState(session.heldNotes ?? '');
  const markHeld = useMarkSessionHeld();

  React.useEffect(() => {
    if (open) setNotes(session.heldNotes ?? '');
  }, [open, session.heldNotes]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!session.id) return;
    markHeld
      .mutateAsync({ id: session.id, notes: notes.trim() })
      .then(() => {
        toast.success('Recorded — it is in the showcase log now');
        onOpenChange(false);
      })
      .catch((e) => toast.error('Could not record', { description: err(e) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {session.heldAt ? 'Correct the record' : 'Record what happened'}
            </DialogTitle>
            <DialogDescription>
              {formatSessionDate(session.heldOn)}. This is what puts the line-up into the
              showcase log, and onto everyone's appraisal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dc-held-notes">What was shown?</Label>
              <Textarea
                id="dc-held-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Three demos and a good argument about retries. Ran ten minutes over."
                rows={4}
                maxLength={5000}
                autoFocus
              />
            </div>
            {session.bookings.length === 0 && (
              <Alert>
                <AlertDescription>
                  Nobody was booked in. Recording it will add an empty Friday to the log.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Save the write-up of this session"} type="submit" disabled={notes.trim().length < 3 || markHeld.isPending}>
              {markHeld.isPending ? 'Saving…' : session.heldAt ? 'Update' : 'Record it'}
            </Button>
            {notes.trim().length < 3 && <span className="self-center text-xs text-muted-foreground">Write a few words about what was shown</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SessionCard({ session }: { session: DevConnectSession }) {
  const [booking, setBooking] = React.useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [heldOpen, setHeldOpen] = React.useState(false);
  const [clearing, setClearing] = React.useState<DevConnectBooking | null>(null);
  const me = useCurrentUser();
  const release = useReleaseSlot();
  const on = today();
  const state = devConnectState({ heldOn: session.heldOn, heldAt: session.heldAt }, on);
  const blocked = blockedMessage(session);

  // Dev Connect admin is SUPER_ADMIN, deliberately not isHr - see the service.
  // A session with no id is a Friday nobody has materialised yet: there is nothing
  // to edit until somebody books into it or the worker opens it.
  const isAdmin = me.role === 'SUPER_ADMIN';
  const canEdit = isAdmin && session.id !== null;
  const canRecord = canEdit && session.heldOn <= on;

  const slots = Array.from({ length: session.capacity }, (_, i) => i);
  const byIndex = new Map(session.bookings.map((b) => [b.slotIndex, b]));

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            {formatSessionDate(session.heldOn)}
            <Badge variant={devConnectStateVariant(state)} className={PILL_CLASS}>
              {DEV_CONNECT_STATE_LABELS[state]}
            </Badge>
          </CardTitle>
          <CardDescription>
            {formatTime(session.startsAtLocal)}
            {session.theme ? ` · ${session.theme}` : ''}
            {' · '}
            {session.openSlots.length === 0
              ? 'Full'
              : `${session.openSlots.length} of ${session.capacity} open`}
          </CardDescription>
        </div>

        {canEdit && (
          <div className="flex shrink-0 gap-1">
            {canRecord && (
              <Button hint={"Record what was discussed in this session"}
                variant={session.heldAt ? 'ghost' : 'outline'}
                size="sm"
                className="gap-1.5"
                onClick={() => setHeldOpen(true)}
              >
                <CheckCircle2 className="size-3.5" />
                {session.heldAt ? 'Edit write-up' : 'Record'}
              </Button>
            )}
            {!session.heldAt && (
              <Button hint={"Change the session settings"}
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={() => setSettingsOpen(true)}
              >
                <Settings2 className="size-3.5" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {slots.map((slotIndex) => {
          const taken = byIndex.get(slotIndex);
          if (taken) {
            return (
              <div
                key={slotIndex}
                className="flex items-start justify-between gap-3 rounded-lg border bg-muted/40 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{taken.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {taken.presenter.name}
                    {taken.presenter.departmentName ? ` · ${taken.presenter.departmentName}` : ''}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {taken.demoUrl && <LinkChip href={taken.demoUrl} label="Demo" />}
                    {taken.slidesUrl && <LinkChip href={taken.slidesUrl} label="Slides" />}
                    {taken.prUrl && <LinkChip href={taken.prUrl} label="PR" />}
                  </div>
                </div>
                {taken.canRelease &&
                  (taken.presenter.id === me.id ? (
                    // Your own: nothing to explain, so no dialog in the way.
                    <Button hint={"Release this slot so someone else can book it"}
                      variant="ghost"
                      size="sm"
                      className="shrink-0 gap-1 text-muted-foreground"
                      disabled={release.isPending}
                      onClick={() =>
                        release
                          .mutateAsync({ id: taken.id })
                          .then(() => toast.success('Slot released'))
                          .catch((e) => toast.error('Could not release', { description: err(e) }))
                      }
                    >
                      <X className="size-3.5" />
                      Release
                    </Button>
                  ) : (
                    // Somebody else's: the service requires a reason, so ask for one
                    // rather than firing a request that is refused.
                    <Button hint={"Clear this booking"}
                      variant="ghost"
                      size="sm"
                      className="shrink-0 gap-1 text-muted-foreground"
                      onClick={() => setClearing(taken)}
                    >
                      <X className="size-3.5" />
                      Clear
                    </Button>
                  ))}
              </div>
            );
          }

          return (
            <button
              key={slotIndex}
              type="button"
              disabled={!session.canBook}
              onClick={() => setBooking(slotIndex)}
              className="flex w-full items-center gap-2 rounded-lg border border-dashed p-3 text-left text-sm text-muted-foreground transition-colors enabled:hover:border-primary enabled:hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
            >
              {session.canBook ? (
                <Plus className="size-4" />
              ) : (
                <Lock className="size-3.5" />
              )}
              <span>Slot {slotIndex + 1}</span>
              <span className="ml-auto text-xs">
                {session.canBook ? 'Take this slot' : (blocked ?? 'Closed')}
              </span>
            </button>
          );
        })}

        {/* The deadline, said before it bites rather than after. */}
        {!session.heldAt && (
          <p className="text-xs text-muted-foreground">
            {session.bookingIsOverride
              ? `Booking closed ${formatCloses(session.closesAt)} — you can still book as an admin.`
              : session.canBook
                ? `Booking closes ${formatCloses(session.closesAt)}.`
                : (blocked ?? '')}
          </p>
        )}
        {session.heldNotes && (
          <p className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
            {session.heldNotes}
          </p>
        )}
      </CardContent>

      <BookDialog
        session={session}
        slotIndex={booking}
        onOpenChange={(open) => !open && setBooking(null)}
      />
      <ClearSlotDialog
        booking={clearing}
        heldOn={session.heldOn}
        open={clearing !== null}
        onOpenChange={(open) => !open && setClearing(null)}
      />
      {canEdit && (
        <>
          <SettingsDialog session={session} open={settingsOpen} onOpenChange={setSettingsOpen} />
          <MarkHeldDialog session={session} open={heldOpen} onOpenChange={setHeldOpen} />
        </>
      )}
    </Card>
  );
}

function LinkChip({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
    >
      <ExternalLink className="size-3" />
      {label}
    </a>
  );
}

function BookDialog({
  session,
  slotIndex,
  onOpenChange,
}: {
  session: DevConnectSession;
  slotIndex: number | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [title, setTitle] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [demoUrl, setDemoUrl] = React.useState('');
  const book = useBookSlot();

  React.useEffect(() => {
    if (slotIndex !== null) {
      setTitle('');
      setNotes('');
      setDemoUrl('');
    }
  }, [slotIndex]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (slotIndex === null) return;
    book
      .mutateAsync({
        heldOn: session.heldOn,
        slotIndex,
        title: title.trim(),
        notes: notes.trim() || null,
        demoUrl: demoUrl.trim() || null,
      })
      .then(() => {
        toast.success(`Booked for ${formatSessionDate(session.heldOn)}`);
        onOpenChange(false);
      })
      .catch((e) => toast.error('Could not book that slot', { description: err(e) }));
  };

  return (
    <Dialog open={slotIndex !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Take slot {(slotIndex ?? 0) + 1}</DialogTitle>
            <DialogDescription>
              {formatSessionDate(session.heldOn)} at {formatTime(session.startsAtLocal)} — a demo, a
              walkthrough, or a problem you got stuck on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/*
              What to bring, said while somebody is claiming the slot rather than in
              a reminder the morning of - by then there is no time left to build a
              deck. The 15 minutes is a cap, not an estimate: four slots in a
              one-hour session leaves no room for one talk to overrun.
            */}
            <Alert>
              <AlertDescription>
                Once you have a slot, prepare a short deck that walks everyone through your
                agenda, then demo or talk through what you built. Keep the whole thing to{' '}
                <span className="font-medium">15 minutes at most</span>.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="dc-title">What are you showing?</Label>
              <Input
                id="dc-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Cutting cold-start time on the tracking API"
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dc-notes">Anything else? (optional)</Label>
              <Textarea
                id="dc-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Context, or what you would like feedback on."
                rows={3}
                maxLength={2000}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dc-demo">Link (optional)</Label>
              <Input
                id="dc-demo"
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                placeholder="https://github.com/... or a slide deck"
                type="url"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Book this slot"} type="submit" disabled={!title.trim() || book.isPending}>
              {book.isPending ? 'Booking…' : 'Book it'}
            </Button>
            {!title.trim() && <span className="self-center text-xs text-muted-foreground">Say what you are showing</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The permanent record: every past Friday's line-up, newest first. */
function ShowcaseLog({ entries, isPending }: { entries: ShowcaseEntry[]; isPending: boolean }) {
  const byFriday = React.useMemo(() => {
    const map = new Map<PlainDate, ShowcaseEntry[]>();
    for (const entry of entries) {
      const list = map.get(entry.heldOn) ?? [];
      list.push(entry);
      map.set(entry.heldOn, list);
    }
    return [...map.entries()];
  }, [entries]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Showcase log</CardTitle>
        <CardDescription>
          Every Dev Connect that has run, and who showed what. It grows week by week.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isPending && <Skeleton className="h-24 w-full" />}

        {!isPending && byFriday.length === 0 && (
          <EmptyState
            art={CalendarArt}
            title="Nothing here yet"
            body="The first Friday to be written up starts the log."
          />
        )}

        {byFriday.map(([heldOn, lineup]) => (
          <div key={heldOn} className="space-y-2">
            <div className="flex items-center gap-2">
              <CalendarDays className="size-3.5 text-muted-foreground" />
              <h3 className="text-sm font-semibold">{formatPlainDate(heldOn)}</h3>
              <span className="text-xs text-muted-foreground">
                {lineup.length} {lineup.length === 1 ? 'talk' : 'talks'}
              </span>
            </div>
            <div className="space-y-2 border-l-2 pl-4">
              {lineup.map((entry) => (
                <div key={entry.id}>
                  <p className="text-sm font-medium">{entry.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.presenter.name}
                    {entry.presenter.departmentName
                      ? ` · ${entry.presenter.departmentName}`
                      : ''}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {entry.demoUrl && <LinkChip href={entry.demoUrl} label="Demo" />}
                    {entry.slidesUrl && <LinkChip href={entry.slidesUrl} label="Slides" />}
                    {entry.prUrl && <LinkChip href={entry.prUrl} label="PR" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function DevConnectPage() {
  const me = useCurrentUser();
  const { data: sessions, isPending } = useDevConnectSessions();
  const { data: log, isPending: logPending } = useShowcaseLog();
  const [opening, setOpening] = React.useState(false);
  const isAdmin = me.role === 'SUPER_ADMIN';

  const entries = log ?? [];
  const mine = entries.filter((e) => e.presenter.id === me.id).length;
  const upcoming = sessions ?? [];
  // The list also carries past sessions that still need writing up, and they sort
  // first. This tile is about where to BOOK, so it needs the next session that has
  // not happened - `upcoming[0]` would advertise open slots on a session that ran
  // last week.
  const nextUp = upcoming.find((s) => s.heldOn >= today());
  const openThisWeek = nextUp?.openSlots.length ?? 0;

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          // Names the day rather than saying "this Friday": a session an admin has
          // moved is not a Friday, and a tile insisting otherwise sends somebody to
          // the wrong day.
          label={nextUp ? `Open ${formatSessionDate(nextUp.heldOn)}` : 'Next session'}
          value={openThisWeek}
          caption={
            nextUp
              ? `${nextUp.capacity - openThisWeek} of ${nextUp.capacity} taken`
              : 'No session scheduled'
          }
          // Green means "the sheet is full", which needs a session to be true of.
          // With none scheduled openThisWeek is also 0, and colouring that as good
          // reads as a full house when it is an empty calendar.
          tone={nextUp && openThisWeek === 0 ? 'good' : 'default'}
        />
        <StatTile
          label="You have presented"
          value={mine}
          caption={mine === 0 ? 'Take a slot and start the record' : 'Shows on your appraisal'}
          tone={mine > 0 ? 'good' : 'default'}
        />
        <StatTile
          label="Talks logged"
          value={entries.length}
          caption="Across every Friday so far"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button hint={"Open a new session people can book"} size="sm" onClick={() => setOpening(true)}>
                <Plus className="mr-1 size-4" />
                Open a session
              </Button>
            </div>
          )}
          {isAdmin && <NewSessionDialog open={opening} onOpenChange={setOpening} />}
          {isPending && <Skeleton className="h-64 w-full" />}
          {/*
            Reachable now, and routinely. The sheet used to pad itself with the next
            four Fridays whether or not anybody had scheduled them, so this list was
            never empty; sessions are opened by an admin, and between two fortnightly
            ones there is genuinely nothing ahead. Saying so beats four blank cards
            for dates nobody has agreed to.
          */}
          {!isPending && upcoming.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Presentation className="mx-auto size-8 text-muted-foreground/50" />
                <p className="mt-3 text-sm font-medium">No session scheduled</p>
                <p className="text-sm text-muted-foreground">
                  {isAdmin
                    ? 'Open one to put it on the sheet.'
                    : 'The next one will show up here once it is opened.'}
                </p>
              </CardContent>
            </Card>
          )}
          {upcoming.map((session) => (
            <SessionCard key={session.heldOn} session={session} />
          ))}
        </div>
        <ShowcaseLog entries={entries} isPending={logPending} />
      </div>
    </PageContainer>
  );
}
